/**
 * The canonical auditable-event taxonomy, rendered for the UI.
 *
 * GENERATED by `tools/check_audit_taxonomy.py --write` from
 * `meridian_logging._taxonomy.AUDIT_EVENT_TYPES_BY_CATEGORY` — DO NOT EDIT.
 * `make audit-taxonomy-regen` rewrites it; `make audit-taxonomy-verify` and
 * the `audit-taxonomy-lint` CI job fail when it drifts from the constant.
 *
 * Every `event_type` string Meridian emits, keyed by its `meridian.v1.audit.*`
 * category. A `<state>` segment is a runtime-substituted placeholder (the
 * peer / provider state routes build their event type from the requested
 * state); it is kept verbatim so the module stays honest about which
 * segments are dynamic, and consumers that need an exact match leave it out.
 *
 * Cross-references:
 *   - ADR-010 §2 amended 2026-08-01 — the ten-category taxonomy.
 *   - REQ-LOG-006 / -007 — the audit envelope.
 *   - REQ-UI-022 — the Audit Log action-kind filter that consumes this.
 */

/** Category → the event types emitted under it (the Python constant, verbatim). */
export const AUDIT_EVENT_TYPES_BY_CATEGORY = {
  AuthenticationEvent: [
    "user.login.attempt",
    "user.login.success",
    "user.login.failure",
    "user.logout",
    "user.session.timeout",
    "user.session.revoked",
    "user.session.refresh_failed",
    "user.display_only.entered",
    "user.display_only.exited",
  ],
  AuthorizationEvent: [
    "user.permission.denied",
    "user.privileged.operation",
  ],
  DataAccessEvent: [
    "query.executed",
    "query.error",
    "query.record_bytes.read",
    "query.export.created",
    "query.blob.downloaded",
  ],
  ConfigurationChangeEvent: [
    "onboard.ca_root.registered",
    "onboard.producer.created",
    "onboard.producer.create.conflict",
    "onboard.producer.cert_bound",
    "onboard.aggregator.created",
    "onboard.aggregator.create.conflict",
    "onboard.aggregator.cert_bound",
    "onboard.bulk_import.completed",
    "aggregator.status.changed",
    "alert.dismissed",
    "audit.export.created",
    "peer.created",
    "peer.updated",
    "peer.deleted",
    "peer.<state>",
    "peer.interface.created",
    "peer.interface.updated",
    "peer.interface.deleted",
    "peer.interface.seeded",
    "peer.interface.<state>",
    "peer.connection.<state>",
    "peer.cert_material.uploaded",
    "provider.created",
    "provider.updated",
    "provider.deleted",
    "provider.<state>",
    "provider.incident_stopped",
    "provider.exported",
    "provider.seeded",
    "provider.interface.created",
    "provider.interface.updated",
    "provider.interface.deleted",
    "provider.interface.<state>",
    "provider.connection.<state>",
    "provider.connection.deleted",
    "provider.artifact.uploaded",
    "provider.cert_material.uploaded",
    "profile.global.created",
    "profile.global.updated",
    "profile.global.deleted",
    "profile.global.reset",
    "query.saved.created",
    "query.saved.updated",
    "query.saved.deleted",
  ],
  ConnectorEvent: [
    "connector.message_quarantined",
    "producer.first_sighting",
  ],
  CdsEvent: [
    "cds.transfer.received",
    "cds.transfer.malformed",
    "cds.transfer.integrity_failure",
    "cds.transfer.schema_violation",
    "cds.transfer.blob_assembled",
    "cds.transfer.blob_integrity_failure",
  ],
  CertLifecycleEvent: [],
  MtlsEvent: [
    "mtls.connection.rejected",
    "mtls.cert_expiring",
    "mtls.cert_expired",
  ],
  DataLifecycleEvent: [
    "storage.partition.exported",
    "storage.chunk.dropped",
  ],
  HaLeaseEvent: [
    "ha.lease.acquired",
    "ha.lease.lost",
  ],
} as const;

/** A `meridian.v1.audit.*` category name. */
export type AuditEventCategory = keyof typeof AUDIT_EVENT_TYPES_BY_CATEGORY;

/** Every emitted event type, flat, in category order. */
export const AUDIT_EVENT_TYPES: readonly string[] = Object.values(
  AUDIT_EVENT_TYPES_BY_CATEGORY,
).flat();
